import { createReducer, on } from '@ngrx/store';

import { DataList } from '@core/interfaces';
import { currentTimeInSeconds } from '@util/list';

import { CarrierActions } from '@carrier/state';
import { CustomerActions } from '@customer/state';
import { FacilityActions } from '@facility/state';
import { InventoryActions } from '@inventory/state';
import { ItemActions } from '@item/state';
import { OrderActions } from '@order/state';
import { ReportActions } from '@report/state';
import { ShipmentActions } from '@shipment/state';
import { SystemActions } from '@system/state';
import { UserActions } from '@user/state';

import * as ListActions from './list.actions';

export interface ListState {
  carrier: {
    carrier: DataList;
    deliveryServiceCode: Record<string, DataList>;
  };
  customer: {
    authorized: DataList;
    customer: DataList;
    customerStatus: DataList;
    customerItemUom: Record<string, DataList>;
    dictionary: Record<string, DataList>;
  };
  facility: {
    authorized: DataList;
    dictionary: Record<string, DataList>;
    facility: DataList;
    facilityStatus: DataList;
  };
  inventory: {
    inventoryClass: DataList;
    inventoryStatus: DataList;
  };
  item: {
    customerItemUom: Record<string, DataList>;
    customerProductGroup: Record<string, DataList>;
    customerRateGroup: Record<string, DataList>;
    productGroup: DataList;
  };
  order: {
    orderItemStatus: DataList;
    orderPriority: DataList;
    orderStatus: DataList;
    orderType: DataList;
  };
  report: {
    folder: DataList;
    format: DataList;
    reports: DataList;
  };
  shipment: {
    countryCode: DataList;
    shipmentTerms: DataList;
    shipmentTypes: DataList;
    stateOrProvince: DataList;
  };
  static: {
    yesNo: DataList;
  };
  system: {
    systemDefault: DataList;
  };
  user: {
    userForm: DataList;
    userGroup: DataList;
    userStatus: DataList;
  };
}

export const initialState: ListState = {
  carrier: {
    carrier: null,
    deliveryServiceCode: {},
  },
  customer: {
    authorized: null,
    customer: null,
    customerStatus: null,
    customerItemUom: {},
    dictionary: {},
  },
  facility: {
    authorized: null,
    dictionary: {},
    facility: null,
    facilityStatus: null,
  },
  inventory: {
    inventoryClass: null,
    inventoryStatus: null,
  },
  item: {
    customerItemUom: {},
    customerProductGroup: {},
    customerRateGroup: {},
    productGroup: null,
  },
  order: {
    orderItemStatus: null,
    orderPriority: null,
    orderStatus: null,
    orderType: null,
  },
  report: {
    folder: null,
    format: null,
    reports: null,
  },
  shipment: {
    countryCode: null,
    shipmentTerms: null,
    shipmentTypes: null,
    stateOrProvince: null,
  },
  static: {
    yesNo: {
      data: null,
      updated: null,
    },
  },
  system: {
    systemDefault: null,
  },
  user: {
    userForm: null,
    userGroup: null,
    userStatus: null,
  },
};

export const reducer = createReducer(
  initialState,

  //
  // lists
  //

  on(
    ListActions.initialize,
    (): ListState => ({
      ...initialState,
    })
  ),

  //
  // static lists
  //

  on(
    ListActions.setStaticLists,
    (state, action): ListState => ({
      ...state,
      static: {
        ...state.static,
        yesNo: {
          data: [...action.yesNo],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // customer lists
  //

  on(
    CustomerActions.RequestCustomerLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    CustomerActions.RequestCustomerListsSuccess,
    (state, action): ListState => ({
      ...state,
      customer: {
        ...state.customer,
        customer: {
          data: [...action.lists.customer],
          updated: currentTimeInSeconds(),
        },
        authorized: {
          data: [...action.lists.authorized],
          updated: currentTimeInSeconds(),
        },
        customerStatus: {
          data: [...action.lists.customerStatus],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  on(CustomerActions.RequestCustomerItemUomList, (state, action): ListState => {
    let key: string;

    if (action.custid && action.item) {
      key = `${action.custid}-${action.item}`;
    } else {
      key = 'all';
    }

    return {
      ...state,
      customer: {
        ...state.customer,
        customerItemUom: {
          ...state.customer.customerItemUom,
          [key]: null,
        },
      },
    };
  }),

  on(CustomerActions.RequestCustomerItemUomListSuccess, (state, action): ListState => {
    let key: string;

    if (action.custid && action.item) {
      key = `${action.custid}-${action.item}`;
    } else {
      key = 'all';
    }

    return {
      ...state,
      customer: {
        ...state.customer,
        customerItemUom: {
          ...state.customer.customerItemUom,
          [key]: {
            data: [...action.list],
            updated: currentTimeInSeconds(),
          },
        },
      },
    };
  }),

  on(
    CustomerActions.RequestCustomerDictionaryFieldList,
    (state, action): ListState => ({
      ...state,
      customer: {
        ...state.customer,
        dictionary: {
          ...state.customer.dictionary,
          [action.custid]: null,
        },
      },
    })
  ),

  on(
    CustomerActions.RequestCustomerDictionaryFieldListSuccess,
    (state, action): ListState => ({
      ...state,
      customer: {
        ...state.customer,
        dictionary: {
          ...state.customer.dictionary,
          [action.custid]: {
            data: [...action.list],
            updated: currentTimeInSeconds(),
          },
        },
      },
    })
  ),

  //
  // facility lists
  //

  on(
    FacilityActions.RequestFacilityLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    FacilityActions.RequestFacilityListsSuccess,
    (state, action): ListState => ({
      ...state,
      facility: {
        ...state.facility,
        facility: {
          data: [...action.lists.facility],
          updated: currentTimeInSeconds(),
        },
        authorized: {
          data: [...action.lists.authorized],
          updated: currentTimeInSeconds(),
        },
        facilityStatus: {
          data: [...action.lists.facilityStatus],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  on(
    FacilityActions.RequestFacilityDictionaryFieldList,
    (state, action): ListState => ({
      ...state,
      facility: {
        ...state.facility,
        dictionary: {
          ...state.facility.dictionary,
          [action.facility]: null,
        },
      },
    })
  ),

  on(
    FacilityActions.RequestFacilityDictionaryFieldListSuccess,
    (state, action): ListState => ({
      ...state,
      facility: {
        ...state.facility,
        dictionary: {
          ...state.facility.dictionary,
          [action.facility]: {
            data: [...action.list],
            updated: currentTimeInSeconds(),
          },
        },
      },
    })
  ),

  //
  // carrier lists
  //

  on(
    CarrierActions.RequestCarrierList,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    CarrierActions.RequestCarrierListSuccess,
    (state, action): ListState => ({
      ...state,
      carrier: {
        ...state.carrier,
        carrier: {
          data: [...action.list],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  on(
    CarrierActions.RequestDeliveryServiceCodeList,
    (state, action): ListState => ({
      ...state,
      carrier: {
        ...state.carrier,
        deliveryServiceCode: {
          ...state.carrier.deliveryServiceCode,
          [action.carrier]: null,
        },
      },
    })
  ),

  on(
    CarrierActions.RequestDeliveryServiceCodeListSuccess,
    (state, action): ListState => ({
      ...state,
      carrier: {
        ...state.carrier,
        deliveryServiceCode: {
          ...state.carrier.deliveryServiceCode,
          [action.carrier]: {
            data: [...action.list],
            updated: currentTimeInSeconds(),
          },
        },
      },
    })
  ),

  //
  // item lists
  //

  on(
    ItemActions.RequestItemLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    ItemActions.RequestItemListsSuccess,
    (state, action): ListState => ({
      ...state,
      item: {
        ...state.item,
        productGroup: {
          data: [...action.lists.productGroup],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // item / customer product group list
  //

  on(ItemActions.RequestItemCustomerProductGroupList, (state, action): ListState => {
    let key: string;

    if (action.custid) {
      key = action.custid;
    } else {
      key = 'all';
    }

    return {
      ...state,
      item: {
        ...state.item,
        customerProductGroup: {
          ...state.item.customerProductGroup,
          [key]: null,
        },
      },
    };
  }),

  on(ItemActions.RequestItemCustomerProductGroupListSuccess, (state, action): ListState => {
    let key: string;

    if (action.custid) {
      key = action.custid;
    } else {
      key = 'all';
    }

    return {
      ...state,
      item: {
        ...state.item,
        customerProductGroup: {
          ...state.item.customerProductGroup,
          [key]: {
            data: [...action.list],
            updated: currentTimeInSeconds(),
          },
        },
      },
    };
  }),

  //
  // item / customer rate group list
  //

  on(
    ItemActions.RequestItemCustomerRateGroupList,
    (state, action): ListState => ({
      ...state,
      item: {
        ...state.item,
        customerRateGroup: {
          ...state.item.customerRateGroup,
          [action.custid ?? 'all']: null,
        },
      },
    })
  ),

  on(
    ItemActions.RequestItemCustomerRateGroupListSuccess,
    (state, action): ListState => ({
      ...state,
      item: {
        ...state.item,
        customerRateGroup: {
          ...state.item.customerRateGroup,
          [action.custid ?? 'all']: {
            data: [...action.list],
            updated: currentTimeInSeconds(),
          },
        },
      },
    })
  ),

  //
  // inventory lists
  //

  on(
    InventoryActions.RequestInventoryLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    InventoryActions.RequestInventoryListsSuccess,
    (state, action): ListState => ({
      ...state,
      inventory: {
        ...state.inventory,
        inventoryClass: {
          data: [...action.lists.inventoryClass],
          updated: currentTimeInSeconds(),
        },
        inventoryStatus: {
          data: [...action.lists.inventoryStatus],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // order lists
  //

  on(
    OrderActions.RequestOrderLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    OrderActions.RequestOrderListsSuccess,
    (state, action): ListState => ({
      ...state,
      order: {
        ...state.order,
        orderType: {
          data: [...action.lists.orderType],
          updated: currentTimeInSeconds(),
        },
        orderStatus: {
          data: [...action.lists.orderStatus],
          updated: currentTimeInSeconds(),
        },
        orderPriority: {
          data: [...action.lists.orderPriority],
          updated: currentTimeInSeconds(),
        },
        orderItemStatus: {
          data: [...action.lists.orderItemStatus],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // report lists
  //

  on(
    ReportActions.RequestReportLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    ReportActions.RequestReportListsSuccess,
    (state, action): ListState => ({
      ...state,
      report: {
        ...state.report,
        folder: {
          data: [...action.lists.folder],
          updated: currentTimeInSeconds(),
        },
        format: {
          data: [...action.lists.format],
          updated: currentTimeInSeconds(),
        },
        reports: {
          data: [...action.lists.reports],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // shipment lists
  //

  on(
    ShipmentActions.RequestShipmentLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    ShipmentActions.RequestShipmentListsSuccess,
    (state, action): ListState => ({
      ...state,
      shipment: {
        ...state.shipment,
        shipmentTerms: {
          data: [...action.lists.shipmentTerms],
          updated: currentTimeInSeconds(),
        },
        shipmentTypes: {
          data: [...action.lists.shipmentTypes],
          updated: currentTimeInSeconds(),
        },
        countryCode: {
          data: [...action.lists.countryCode],
          updated: currentTimeInSeconds(),
        },
        stateOrProvince: {
          data: [...action.lists.stateOrProvince],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // system lists
  //

  on(
    SystemActions.RequestSystemLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    SystemActions.RequestSystemListsSuccess,
    (state, action): ListState => ({
      ...state,
      system: {
        ...state.system,
        systemDefault: {
          data: [...action.lists.systemDefault],
          updated: currentTimeInSeconds(),
        },
      },
    })
  ),

  //
  // user lists
  //

  on(
    UserActions.RequestUserLists,
    (state): ListState => ({
      ...state,
    })
  ),

  on(
    UserActions.RequestUserListsSuccess,
    (state, action): ListState => ({
      ...state,
      user: {
        ...state.user,
        userForm: {
          data: [...action.lists.userForm],
          updated: currentTimeInSeconds(),
        },
        userGroup: {
          data: [...action.lists.userGroup],
          updated: currentTimeInSeconds(),
        },
        userStatus: {
          data: [...action.lists.userStatus],
          updated: currentTimeInSeconds(),
        },
      },
    })
  )
);
